import SEO from "@/components/SEO";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import wineToursImg from "@/assets/wine-tours.png";
import wineToursVideo from "@/assets/wine-tours-cinematic.mp4.asset.json";

const WineTours = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/wine-tours#service",
    "name": "Texas Hill Country Wine Tours from San Antonio",
    "alternateName": "Fredericksburg Wine Tours",
    "serviceType": "Wine Tour Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "openingHours": "Mo-Su 00:00-23:59"
    },
    "areaServed": {
      "@type": "City",
      "name": "San Antonio",
      "containedInPlace": {
        "@type": "State",
        "name": "Texas"
      }
    },
    "description": "Private chauffeured wine tours from San Antonio to Fredericksburg & the Texas Hill Country. Custom itineraries, door-to-door luxury SUV. Book: 210-296-7409.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/wine-tours",
      "priceCurrency": "USD",
      "lowPrice": "85",
      "highPrice": "100",
      "offerCount": "1",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock"
    },
    "priceRange": "$85-$100/hour"
  };

  return (
    <>
      <SEO
        title="Texas Hill Country Wine Tours from San Antonio | Fredericksburg | Rick's"
        description="Private chauffeured wine tours from San Antonio to Fredericksburg & the Texas Hill Country. Custom itineraries, door-to-door luxury SUV. Book: 210-296-7409."
        canonicalPath="/wine-tours"
        schema={serviceSchema}
        includeBreadcrumbs={false}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb items={[{ name: "Wine Tours", url: "/wine-tours" }]} />
          {/* Hero Section */}
          <section className="relative min-h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <video
                src={wineToursVideo.url}
                poster={wineToursImg}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                disableRemotePlayback
                aria-label="Private chauffeured wine tours through the Texas Hill Country"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/40 to-background/60" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
                Texas Hill Country Wine Tours from San Antonio
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Private, chauffeured wine tours to Fredericksburg and the Texas Hill Country in a 2025 Audi Q7 — door-to-door, custom itineraries, and no designated driver needed.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Online Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    The Texas Hill Country is home to one of the fastest-growing wine regions in America — and the best way to experience it is with someone else doing the driving. Rick's Black Car Service offers private, chauffeured wine tours from San Antonio to Fredericksburg and the surrounding Hill Country, so you can taste freely, linger where you like, and ride home in comfort.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Door-to-Door, Winery-to-Winery
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Your tour starts at your front door — home, hotel, or Airbnb anywhere in the San Antonio area. From there, we handle everything: the scenic drive up Highway 290's wine corridor, transportation between tasting rooms, and a safe, relaxing ride home at the end of the day. No designated driver debates, no navigating ranch roads after four tastings.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Custom Itineraries or Local Recommendations
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Already have your favorite wineries picked out? Perfect — send us the list and we'll build the schedule. Not sure where to start? Your chauffeur knows the region well and can recommend wineries to match your taste, from established Fredericksburg estates to boutique tasting rooms, plus great lunch stops on Main Street and scenic detours through towns like Comfort and Luckenbach.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Private Luxury, Not a Crowded Tour Bus
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Group wine bus tours run on someone else's schedule and someone else's playlist. Our tours are private: your group, your pace, your stops. Travel in our 2025 Audi Q7 Lux Black SUV with climate control, bottled water, and room to bring cases of your favorite finds home. Ideal for couples, anniversary celebrations, girls' trips, and small corporate outings.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    Flexible Hourly Booking
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-10">
                    Wine tours are booked as hourly charters, so you're never rushed out of a tasting to meet a schedule. Want to add a vineyard, extend lunch, or stop for photos at a bluebonnet field in spring? Your chauffeur is at your disposal for the entire reservation. Hourly service starts around $85–$100 per hour with a minimum booking.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                    More Than Fredericksburg
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-12">
                    We also run tours to wineries around Boerne, Spring Branch, New Braunfels, and Driftwood — and we're happy to combine a wine tour with dinner reservations back in San Antonio or a stay at a Hill Country bed and breakfast.
                  </p>
                </div>

                {/* Bottom CTA */}
                <div className="text-center py-12 border-t border-border/50">
                  <p className="text-xl md:text-2xl text-foreground font-semibold mb-6">
                    Plan your Hill Country wine tour — call 210-296-7409 and tell us your date, group size, and wish list. We'll handle the rest.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-5 h-5 mr-2" />
                        Call 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-lg px-8 py-6" asChild>
                      <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                        Reserve Online
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WineTours;
